import { useLanguage } from 'features/hooks/useLanguage';
import { CodelistRecord } from '../../domain/codelistRecordSchema';
import { CodelistIcon, CodelistIcons } from 'features/codelist/icons/codelist-icons';
import { LanguageType } from 'types/enums';
import { Pencil } from 'lucide-react';
import { DateTime } from 'luxon';
import { Tooltip } from './Tooltip';
import { CODELIST_SIDEBAR_DICTIONARY } from '../Dictionary';

export const CodelistDisplay = ({
	codelist,
	onEditClicked
}: {
	codelist?: CodelistRecord;
	onEditClicked: () => void;
}) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const addressString = _getAddressStringForCodelist(codelist);
	const nameString = _getFullName(codelist);

	return (
		<div>
			<div className="flex flex-col gap-4">
				<CodelistDisplayItem
					Icon={CodelistIcons.customId}
					value={codelist?.customId}
					label={translation.iconLabels.customId}
				/>

				<CodelistDisplayItem
					Icon={CodelistIcons.firstName}
					value={nameString || translation.noName}
					label={translation.iconLabels.name}
				/>

				<CodelistDisplayItem
					Icon={CodelistIcons.ssn}
					value={codelist?.ssn}
					label={translation.iconLabels.ssn}
				/>

				<CodelistDisplayItem
					Icon={CodelistIcons.dateOfBirth}
					value={
						codelist?.dateOfBirth
							? DateTime.fromJSDate(new Date(codelist.dateOfBirth)).toLocaleString(
									DateTime.DATE_MED
							  )
							: undefined
					}
					label={translation.iconLabels.dateOfBirth}
				/>

				<CodelistDisplayItem
					Icon={CodelistIcons.mobilePhone}
					value={_formatPhoneNumber(codelist?.mobilePhone)}
					label={translation.iconLabels.mobilePhone}
				/>

				<CodelistDisplayItem
					Icon={CodelistIcons.email}
					value={codelist?.email}
					label={translation.iconLabels.email}
				/>

				<CodelistDisplayItem
					Icon={CodelistIcons.address}
					value={addressString || translation.noAddress}
					label={translation.iconLabels.address}
				/>
			</div>

			<button
				onClick={onEditClicked}
				type="button"
				className="text-sm rounded-md font-semibold flex items-center justify-center gap-4 mt-6 hover:bg-gray-200 p-4 w-full"
			>
				<Pencil className="size-6" />
				<span>{translation.editButton}</span>
			</button>
		</div>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		noAddress: 'No address',
		noName: 'No name',
		editButton: 'Edit',

		iconLabels: {
			customId: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].customId.label,
			ssn: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].ssn.label,
			name: 'Name',
			dateOfBirth: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].dateOfBirth.label,
			mobilePhone: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].mobilePhone.label,
			email: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].email.label,
			address: CODELIST_SIDEBAR_DICTIONARY[LanguageType.English].address.label
		}
	},

	[LanguageType.Norwegian]: {
		noAddress: 'Ingen adresse',
		noName: 'Ingen navn',
		editButton: 'Rediger',

		iconLabels: {
			customId: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].customId.label,
			ssn: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].ssn.label,
			name: 'Navn',
			dateOfBirth: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].dateOfBirth.label,
			mobilePhone: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].mobilePhone.label,
			email: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].email.label,
			address: CODELIST_SIDEBAR_DICTIONARY[LanguageType.Norwegian].address.label
		}
	}
};

// Exported only for testing
export const _formatPhoneNumber = (phoneNumber?: string) => {
	if (!phoneNumber) return undefined;
	return phoneNumber.replace(/(\d{2})(\d{3})(\d{2})(\d{3})/, '$1 $2 $3 $4');
};

// Exported only for testing
export const _getFullName = (
	codelist?: Partial<Pick<CodelistRecord, 'firstName' | 'lastName'>>
) => {
	if (!codelist) return undefined;

	let name = '';

	if (codelist.firstName) {
		name += codelist.firstName;
	}

	if (codelist.lastName) {
		if (name.length > 0) {
			name += ' ';
		}
		name += codelist.lastName;
	}

	return name.length > 0 ? name : undefined;
};

// Exported only for testing
export const _getAddressStringForCodelist = (
	codelist?: Partial<Pick<CodelistRecord, 'address' | 'postalCode' | 'postalPlace' | 'country'>>
) => {
	if (!codelist) return undefined;

	let addressString = '';

	if (codelist.address) {
		addressString += codelist.address;
	}

	if (codelist.postalCode) {
		if (addressString.length > 0) {
			addressString += ', ';
		}
		addressString += codelist.postalCode;
	}

	if (codelist.postalPlace) {
		if (addressString.length > 0) {
			addressString += ', ';
		}
		addressString += codelist.postalPlace;
	}

	if (codelist.country) {
		if (addressString.length > 0) {
			addressString += ', ';
		}
		addressString += codelist.country;
	}

	return addressString.length > 0 ? addressString : undefined;
};

const CodelistDisplayItem = ({
	Icon,
	value,
	label
}: {
	Icon: CodelistIcon;
	value?: string;
	label: string;
}) => {
	if (!value) return null;

	return (
		<div className="flex gap-4 text-base items-start">
			<Tooltip content={label}>
				<Icon className="size-7" />
			</Tooltip>

			<p>{value}</p>
		</div>
	);
};
